import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArchiveIcon as Archive2,
  ArrowLeft,
  Check,
  CheckCircle2,
  Download,
  FileSignature,
  FileText,
  Inbox,
  Send,
  ShieldAlert,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, EmptyState, useToast } from '@solara/ui-shared';
import { generateInvoiceTimeline, type TimelineEvent, type TimelineEventKind } from '@solara/sample-data';
import { useInvoice } from '@solara/api-client/react';
import { invoicesApi } from '@solara/api-client';
import { saveBlob, buildTddCsv } from '../data/exports';
import { adaptInvoice, minorToAed } from '../data/adapters';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';

function formatSafe(fmt: Intl.DateTimeFormat, value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return fmt.format(d);
}

const ICONS: Record<TimelineEventKind, React.ReactNode> = {
  'intake':              <Inbox className="h-4 w-4" />,
  'validate.syntax':     <FileText className="h-4 w-4" />,
  'validate.business':   <FileText className="h-4 w-4" />,
  'validate.trn':        <ShieldCheck className="h-4 w-4" />,
  'validate.schematron': <ShieldAlert className="h-4 w-4" />,
  'sign':                <FileSignature className="h-4 w-4" />,
  'as4.send':            <Send className="h-4 w-4" />,
  'mls.update':          <CheckCircle2 className="h-4 w-4" />,
  'tdd.submit':          <FileText className="h-4 w-4" />,
  'archive':             <Archive2 className="h-4 w-4" />,
};

export default function InvoiceDetailPage() {
  const { t } = useTranslation('invoiceDetail');
  const { t: tTs } = useTranslation('tickstar');
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const invoiceQuery = useInvoice(invoiceId);
  const dto = invoiceQuery.data;
  const invoice = useMemo(() => (dto ? adaptInvoice(dto) : undefined), [dto]);
  const { locale } = useI18n();
  const { toast } = useToast();

  const aedFmt = new Intl.NumberFormat(locale, { style: 'currency', currency: 'AED' });
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });

  const timeline = useMemo(
    () => (invoice ? generateInvoiceTimeline(invoice) : null),
    [invoice],
  );
  // Backend pipeline derives TDD + archive from invoice status; render
  // them inline from the DTO rather than fetching extra resources.
  const tdd = useMemo(() => {
    if (!dto) return undefined;
    if (!dto.acknowledgedAt && dto.status !== 'ARCHIVED' && dto.status !== 'ACKNOWLEDGED') {
      return undefined;
    }
    return {
      status: dto.status === 'ARCHIVED' ? 'ACCEPTED' : 'SUBMITTED',
      deadline: dto.acknowledgedAt ?? dto.issueDate,
      submittedAt: dto.acknowledgedAt ?? null,
      emaraConfirmationId: dto.peppolTransactionId ?? null,
      errorMessage: dto.errorMessage ?? undefined,
    };
  }, [dto]);
  const archive = useMemo(() => {
    if (!dto || dto.status !== 'ARCHIVED') return undefined;
    return {
      archiveId: dto.id,
      retentionUntil: '+5y',
      legalHold: false,
      invoiceXmlHash: dto.messageId ?? dto.id,
    };
  }, [dto]);

  if (invoiceQuery.isLoading) return <LoadingBlock />;
  if (invoiceQuery.error) return <ErrorBanner error={invoiceQuery.error} />;
  if (!invoice || !dto) {
    return (
      <div className="space-y-4">
        <Link to="/invoices" className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg">
          <ArrowLeft className="h-4 w-4 rtl-mirror" />
          {t('back')}
        </Link>
        <EmptyState icon={<AlertTriangle className="h-6 w-6" />} title={t('notFound')} />
      </div>
    );
  }
  // Suppress unused warning for minorToAed when not referenced elsewhere.
  void minorToAed;

  const buyerLabel = locale === 'ar-AE' ? invoice.buyerNameAr : invoice.buyerName;
  // Capture after narrowing so the closures below don't re-widen to `undefined`.
  const invoiceDto = dto;

  async function downloadXml() {
    try {
      const blob = await invoicesApi.downloadXml(invoiceDto.id);
      saveBlob(blob, `${invoiceDto.invoiceNumber}.xml`);
      toast({ title: t('download.started', { format: 'XML' }), tone: 'success' });
    } catch (err) {
      toast({ title: t('download.started', { format: 'XML' }), description: (err as Error).message, tone: 'danger' });
    }
  }

  async function downloadPdf() {
    try {
      const blob = await invoicesApi.downloadPdf(invoiceDto.id);
      saveBlob(blob, `${invoiceDto.invoiceNumber}.pdf`);
      toast({ title: t('download.started', { format: 'PDF' }), tone: 'success' });
    } catch (err) {
      toast({ title: t('download.started', { format: 'PDF' }), description: (err as Error).message, tone: 'danger' });
    }
  }

  function downloadTdd() {
    const csv = buildTddCsv({
      invoiceNumber: invoiceDto.invoiceNumber,
      buyerTrn: invoiceDto.buyerTrn,
      buyerName: invoiceDto.buyerName,
      sellerTrn: invoiceDto.sellerTrn,
      sellerName: invoiceDto.sellerName,
      issueDate: invoiceDto.issueDate,
      currency: invoiceDto.currency,
      lineItems: invoiceDto.lineItems,
    });
    saveBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${invoiceDto.invoiceNumber}-tdd.csv`);
    toast({ title: t('download.started', { format: 'TDD' }), tone: 'success' });
  }

  return (
    <div className="space-y-6">
      <Link to="/invoices" className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-4 w-4 rtl-mirror" />
        {t('back')}
      </Link>

      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{invoice.invoiceNumber}</h1>
          <div className="text-fg-muted mt-1" dir="auto">
            {buyerLabel} · {t('header.trn', { trn: invoice.buyerTrn })}
          </div>
          <div className="text-xs text-fg-subtle mt-1">
            {t('header.issued', {
              when: formatSafe(dateFmt, invoice.issueDate),
              currency: invoice.currency,
            })}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={invoice.mlsStatus} t={t} />
          <Button variant="outline" size="sm" onClick={downloadXml} className="gap-2">
            <Download className="h-4 w-4" /> {t('download.xml')}
          </Button>
          <Button variant="outline" size="sm" onClick={downloadTdd} className="gap-2">
            <Download className="h-4 w-4" /> {t('download.tdd')}
          </Button>
          <Button variant="outline" size="sm" onClick={downloadPdf} className="gap-2">
            <Download className="h-4 w-4" /> {t('download.pdf')}
          </Button>
        </div>
      </header>

      <section className="grid gap-4 sm:grid-cols-3">
        <SmallCard label={t('totals.net')}>
          <span className="text-xl font-semibold num">{aedFmt.format(invoice.totalNetAmount)}</span>
        </SmallCard>
        <SmallCard label={t('totals.vat')}>
          <span className="text-xl font-semibold num">{aedFmt.format(invoice.totalVatAmount)}</span>
        </SmallCard>
        <SmallCard label={t('totals.gross')} highlight>
          <span className="text-xl font-semibold num text-brand-800">
            {aedFmt.format(invoice.totalGrossAmount)}
          </span>
        </SmallCard>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2 rounded-2xl">
          <CardHeader>
            <CardTitle>{t('timeline.title')}</CardTitle>
          </CardHeader>
          <CardBody>
            <ol className="relative ms-2">
              {timeline?.events.map((ev, idx) => (
                <TimelineRow
                  key={idx}
                  ev={ev}
                  dateFmt={dateFmt}
                  isLast={idx === (timeline.events.length - 1)}
                  label={t(`timeline.kinds.${ev.kind}`)}
                  partnerLabel={isTickstarStep(ev.kind) ? tTs('brand.partner') : undefined}
                />
              ))}
            </ol>
          </CardBody>
        </Card>

        <div className="space-y-6">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle>{t('tdd.title')}</CardTitle>
            </CardHeader>
            <CardBody className="space-y-2 text-sm">
              {tdd ? (
                <>
                  <Row label={t('tdd.status')} value={<Badge tone={tddTone(tdd.status)}>{tdd.status}</Badge>} />
                  <Row label={t('tdd.deadline')} value={formatSafe(dateFmt, tdd.deadline)} />
                  <Row
                    label={t('tdd.submitted')}
                    value={formatSafe(dateFmt, tdd.submittedAt)}
                  />
                  {tdd.emaraConfirmationId ? (
                    <Row label={t('tdd.ref')} value={<code className="text-xs">{tdd.emaraConfirmationId}</code>} />
                  ) : null}
                  {tdd.errorMessage ? (
                    <p className="text-xs text-danger">{tdd.errorMessage}</p>
                  ) : null}
                </>
              ) : (
                <p className="text-fg-muted text-sm">{t('tdd.notYet')}</p>
              )}
            </CardBody>
          </Card>

          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle>{t('archive.title')}</CardTitle>
            </CardHeader>
            <CardBody className="space-y-2 text-sm">
              {archive ? (
                <>
                  <Row label={t('archive.id')} value={<code className="text-[10px]">{archive.archiveId}</code>} />
                  <Row label={t('archive.retention')} value={archive.retentionUntil} />
                  <Row label={t('archive.legalHold')} value={archive.legalHold ? <Badge tone="warning">{t('archive.yes')}</Badge> : t('archive.no')} />
                  <Row
                    label={t('archive.xmlHash')}
                    value={<code className="text-[10px] break-all">{archive.invoiceXmlHash.slice(0, 16)}…</code>}
                  />
                  <div className="pt-2">
                    <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 text-[10px] font-medium" title={tTs('brand.partnerLong')}>
                      {tTs('pillars.archive.title')}
                    </span>
                  </div>
                </>
              ) : (
                <p className="text-fg-muted text-sm">{t('archive.notYet')}</p>
              )}
            </CardBody>
          </Card>
        </div>
      </section>

      <section>
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>{t('items.title', { count: invoice.lineItems.length })}</CardTitle>
          </CardHeader>
          <CardBody>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-fg-subtle">
                    <th className="text-start font-medium pb-2">{t('items.description')}</th>
                    <th className="text-end font-medium pb-2">{t('items.qty')}</th>
                    <th className="text-end font-medium pb-2">{t('items.unitPrice')}</th>
                    <th className="text-end font-medium pb-2">{t('items.vat')}</th>
                    <th className="text-end font-medium pb-2">{t('items.net')}</th>
                    <th className="text-end font-medium pb-2">{t('items.vatAmount')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {invoice.lineItems.map((li, idx) => (
                    <tr key={idx}>
                      <td className="py-2.5">
                        <div className="font-medium">{li.description}</div>
                        {li.descriptionAr ? (
                          <div className="text-xs text-fg-subtle" dir="rtl">{li.descriptionAr}</div>
                        ) : null}
                      </td>
                      <td className="py-2.5 text-end num">{li.quantity}</td>
                      <td className="py-2.5 text-end num">{aedFmt.format(li.unitPrice)}</td>
                      <td className="py-2.5 text-end">
                        {li.vatRate === 'EXEMPT' ? t('items.exempt') : `${li.vatRate}%`}
                      </td>
                      <td className="py-2.5 text-end num">{aedFmt.format(li.lineNet)}</td>
                      <td className="py-2.5 text-end num">{aedFmt.format(li.lineVat)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      </section>

      <section>
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>{t('attachments.title', { count: invoice.attachments.length })}</CardTitle>
          </CardHeader>
          <CardBody>
            {invoice.attachments.length === 0 ? (
              <p className="text-sm text-fg-muted">{t('attachments.none')}</p>
            ) : (
              <ul className="space-y-2">
                {invoice.attachments.map((a) => (
                  <li
                    key={a.path}
                    className="flex items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2"
                  >
                    <span className="text-fg-subtle shrink-0">
                      <FileText className="h-4 w-4" />
                    </span>
                    <span className="text-sm font-medium truncate flex-1" dir="ltr">
                      {a.name}
                    </span>
                    <span className="text-xs text-fg-subtle uppercase shrink-0" dir="ltr">
                      {a.mime.split('/')[1]}
                    </span>
                    <span className="text-xs text-fg-subtle num shrink-0" dir="ltr">
                      {humanSize(a.sizeBytes, locale)}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => {
                        toast({
                          title: t('attachments.downloadStarted', { name: a.name }),
                          description: t('attachments.downloadStartedBody'),
                          tone: 'info',
                        });
                      }}
                    >
                      <Download className="h-3.5 w-3.5" />
                      {t('attachments.download')}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </section>
    </div>
  );
}

function humanSize(bytes: number, locale: string): string {
  const fmt = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  if (bytes >= 1024 * 1024) return `${fmt.format(bytes / 1024 / 1024)} MB`;
  if (bytes >= 1024) return `${fmt.format(bytes / 1024)} KB`;
  return `${bytes} B`;
}

function isTickstarStep(kind: TimelineEventKind): boolean {
  return (
    kind === 'validate.schematron' ||
    kind === 'as4.send' ||
    kind === 'mls.update' ||
    kind === 'tdd.submit' ||
    kind === 'archive'
  );
}

function TimelineRow({
  ev,
  dateFmt,
  isLast,
  label,
  partnerLabel,
}: {
  ev: TimelineEvent;
  dateFmt: Intl.DateTimeFormat;
  isLast: boolean;
  label: string;
  partnerLabel?: string;
}) {
  const tone =
    ev.status === 'failed'
      ? 'bg-danger text-white'
      : ev.status === 'pending'
      ? 'bg-warning text-white'
      : partnerLabel
      ? 'bg-amber-500 text-white'
      : 'bg-success text-white';
  return (
    <li className="relative ps-10 pb-5 last:pb-0">
      {!isLast ? (
        <span
          className="absolute start-[14px] top-7 bottom-0 w-px bg-border"
          aria-hidden
        />
      ) : null}
      <span
        className={`absolute start-0 top-1 h-7 w-7 rounded-full grid place-items-center text-xs ${tone}`}
      >
        {ev.status === 'failed' ? <XCircle className="h-4 w-4" /> : <Check className="h-3.5 w-3.5" />}
      </span>
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">{label}</span>
          {partnerLabel ? (
            <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 text-[10px] font-medium">
              {partnerLabel}
            </span>
          ) : null}
          {ev.errorCode ? <Badge tone="danger">{ev.errorCode}</Badge> : null}
          {ev.durationMs ? (
            <span className="text-xs text-fg-subtle">· {ev.durationMs} ms</span>
          ) : null}
        </div>
        <div className="text-xs text-fg-subtle">{formatSafe(dateFmt, ev.at)}</div>
        {ev.detail ? <p className="text-sm text-fg-muted mt-1">{ev.detail}</p> : null}
      </div>
    </li>
  );
}

function StatusBadge({
  status,
  t,
}: {
  status: 'AP' | 'AB' | 'RE' | 'PENDING';
  t: (k: string) => string;
}) {
  const map = {
    AP: { tone: 'success' as const, key: 'status.ap' },
    AB: { tone: 'info' as const, key: 'status.ab' },
    RE: { tone: 'danger' as const, key: 'status.re' },
    PENDING: { tone: 'neutral' as const, key: 'status.pending' },
  };
  const cfg = map[status];
  return <Badge tone={cfg.tone}>{t(cfg.key)}</Badge>;
}

function tddTone(status: string): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  if (status === 'ACCEPTED') return 'success';
  if (status === 'SUBMITTED') return 'info';
  if (status === 'FAILED' || status === 'DEAD_LETTER') return 'danger';
  return 'warning';
}

function SmallCard({
  label,
  children,
  highlight,
}: {
  label: string;
  children: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <Card className={highlight ? 'rounded-2xl border-brand-200 bg-brand-50' : 'rounded-2xl'}>
      <CardBody className="p-4 space-y-1">
        <div className="text-xs uppercase tracking-wide text-fg-subtle">{label}</div>
        {children}
      </CardBody>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-fg-muted">{label}</span>
      <span className="text-end">{value}</span>
    </div>
  );
}
